// HUDController — the floating HUD overlay that sits over the fixed
// WebGL canvas. It owns three things:
//
//   1) "What scene are we in?" — answered by subscribing to the
//      useScrollTimeline scene bus.
//   2) "What should be on screen right now?" — answered by reading
//      SCENES[idx].showHUD and rendering the matching HUD elements.
//   3) "Is a panel open?" — answered by listening for the
//      `planet:open-panel` global event and Esc / click-backdrop
//      dismissals.
//
// The controller renders the scene-appropriate HUD chips with smooth
// fade in/out. It does NOT render the planet itself; the planet lives
// in <PlanetScene />.

import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  CONTACT,
  HUD_PANELS,
  NOTES,
  SATELLITES,
  SCENES,
  TIMELINE_MILESTONES,
  type HUDId,
  type SceneConfig,
} from './planetSections';
import { HUDPanel } from './HUDPanel';
import { buildShareUrl } from './useUrlSync';
import { ScrollCue } from './ScrollCue';
import { SatelliteInfoCard } from './SatelliteInfoCard';
import { NoteDetailCard } from './NoteDetailCard';
import { Magnetic } from './Magnetic';
import { ModeToggle } from './ModeToggle';
import { Letters, SceneTypography } from './SceneTypography';
import { OrbitRail } from './OrbitRail';
import { GravityCursor } from './GravityCursor';
import { CinematicFrame } from './CinematicFrame';
import './hud.css';
import './kinetic-redesign.css';

// ---------------------------------------------------------------------------
// Code-split heavy / mode-gated children.
// Each gets its own dynamic chunk + a Suspense boundary with a minimal
// placeholder that matches the component's eventual position so layout
// doesn't shift while the chunk loads.
// ---------------------------------------------------------------------------

const DetailPanel = lazy(() =>
  import('./DetailPanel').then((m) => ({ default: m.DetailPanel })),
);
const AmbientAudioController = lazy(() =>
  import('./AmbientAudioController').then((m) => ({
    default: m.AmbientAudioController,
  })),
);
const TourOverlay = lazy(() =>
  import('./TourOverlay').then((m) => ({ default: m.TourOverlay })),
);
const ExplorationToast = lazy(() =>
  import('./ExplorationToast').then((m) => ({
    default: m.ExplorationToast,
  })),
);
const ExplorationInfoCard = lazy(() =>
  import('./ExplorationInfoCard').then((m) => ({
    default: m.ExplorationInfoCard,
  })),
);

// Minimal, layout-stable placeholders. Invisible-by-default so the HUD
// renders instantly and the chunk paints into a known container.
function PanelSlot() {
  return <div className="hud-lazy-slot hud-lazy-slot--panel" aria-hidden="true" />;
}
function AmbientSlot() {
  return <div className="hud-lazy-slot hud-lazy-slot--ambient" aria-hidden="true" />;
}
function TourSlot() {
  return <div className="hud-lazy-slot hud-lazy-slot--tour" aria-hidden="true" />;
}
function ExplorationToastSlot() {
  return (
    <div className="hud-lazy-slot hud-lazy-slot--explore-toast" aria-hidden="true" />
  );
}
function ExplorationCardSlot() {
  return (
    <div className="hud-lazy-slot hud-lazy-slot--explore-card" aria-hidden="true" />
  );
}

// ---------------------------------------------------------------------------
// Panel-open bus — satellites / fragments dispatch an "open panel"
// event; the controller listens and reflects it in React state.
// ---------------------------------------------------------------------------

const panelOpenListeners = new Set<(id: string | null) => void>();

function emitPanelOpen(id: string | null) {
  if (typeof window !== 'undefined') {
    window.__planetOpenPanelId = id;
  }
  for (const cb of panelOpenListeners) {
    try {
      cb(id);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[HUDController] panel listener threw:', e);
    }
  }
}

// ---------------------------------------------------------------------------
// Hooks — small, scene-aware helpers
// ---------------------------------------------------------------------------

/**
 * useScrollProgress — read window.__planetProgress without causing
 * re-renders. Returns a stable ref-like getter.
 */
function useActiveSceneIndex(initial: number): number {
  const [idx, setIdx] = useState(initial);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIdx(window.__planetSceneIndex ?? 0);
    let last = window.__planetSceneIndex ?? 0;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setIdx(cur);
      }
    }, 200);
    return () => window.clearInterval(id);
  }, []);
  return idx;
}

function useIntroProgress(): number {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const tick = () => {
      const cur = window.__planetProgress ?? 0;
      setP((prev) => (Math.abs(prev - cur) > 0.001 ? cur : prev));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return p;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface HUDControllerProps {
  /** Optional initial chapter — defaults to 0. */
  initialChapter?: number;
}

export function HUDController({ initialChapter = 0 }: HUDControllerProps) {
  const activeIdx = useActiveSceneIndex(initialChapter);
  const [openPanelId, setOpenPanelId] = useState<string | null>(null);
  const [mode, setMode] = useState<'narrative' | 'exploration' | 'tour'>(() => {
    if (typeof window === 'undefined') return 'narrative';
    const v = window.__planetMode;
    return v === 'exploration' || v === 'tour' ? v : 'narrative';
  });
  const [modeShiftKey, setModeShiftKey] = useState(0);

  // ---------------------------------------------------------------------
  // Mode tracking — poll the global mode value so React re-renders when
  // the user toggles via the pill, a keyboard shortcut, or a script.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last: 'narrative' | 'exploration' | 'tour' = (() => {
      const v = window.__planetMode;
      return v === 'exploration' || v === 'tour' ? v : 'narrative';
    })();
    setMode(last);
    const id = window.setInterval(() => {
      const cur: 'narrative' | 'exploration' | 'tour' = (() => {
        const v = window.__planetMode;
        return v === 'exploration' || v === 'tour' ? v : 'narrative';
      })();
      if (cur !== last) {
        last = cur;
        setMode(cur);
        // Bump a key so we can animate the bg-shift on each toggle.
        setModeShiftKey((n) => n + 1);
      }
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  // ---------------------------------------------------------------------
  // Auto-close any panel when the scene changes.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (openPanelId) {
      setOpenPanelId(null);
      emitPanelOpen(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdx]);

  // ---------------------------------------------------------------------
  // Open-panel bus — subscribe + listen for global events.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const cb = (id: string | null) => {
      setOpenPanelId(id);
    };
    panelOpenListeners.add(cb);
    return () => {
      panelOpenListeners.delete(cb);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      setOpenPanelId(detail || null);
      emitPanelOpen(detail || null);
    };
    window.addEventListener('planet:open-panel', onOpen as EventListener);
    return () =>
      window.removeEventListener('planet:open-panel', onOpen as EventListener);
  }, []);

  // ESC closes any open panel.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openPanelId) {
        setOpenPanelId(null);
        emitPanelOpen(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPanelId]);

  const closePanel = useCallback(() => {
    setOpenPanelId(null);
    emitPanelOpen(null);
  }, []);

  // ---------------------------------------------------------------------
  // Derived state.
  // ---------------------------------------------------------------------
  const activeScene: SceneConfig = SCENES[activeIdx] ?? SCENES[0];
  const showHUD = new Set<HUDId>(activeScene.showHUD);
  const isPanelOpen = !!openPanelId;
  const isActiveScenePanelOpen = openPanelId === activeScene.key;
  const isExplore = mode === 'exploration';
  const isTour = mode === 'tour';

  // Tour mode forcibly opens per-scene HUD panels (no click required).
  // Chapter nav / progress dots / index kickers are intentionally omitted —
  // the composition should read as artwork, not a product dashboard.

  // Share URL for the active scene/mode — fed into each HUDPanel so the
  // copy-link button always reflects what the user is looking at.
  // Memoized on the (idx, mode) pair so we don't churn replaceState
  // every time the controller re-renders for unrelated reasons.
  const shareUrl = buildShareUrl(activeIdx, isExplore);
  const cinematicSafeStyle = {
    '--cinema-safe': `${activeScene.cinemaBarVh}vh`,
    '--cinema-safe-mobile': `${activeScene.cinemaBarMobileVh}svh`,
  } as CSSProperties;

  return (
    <div
      className={[
        'hud-root',
        isExplore ? 'is-explore' : isTour ? 'is-tour' : 'is-narrative',
        // Bump the key so the bg-shift animation re-fires on every mode toggle.
        `mode-shift-${modeShiftKey}`,
      ]
        .filter(Boolean)
        .join(' ')}
      role="presentation"
      data-mode={mode}
      style={cinematicSafeStyle}
    >
      <CinematicFrame
        scene={activeScene}
        hidden={isExplore || isPanelOpen}
      />
      <ModeToggle />
      <OrbitRail
        activeIndex={activeIdx}
        hidden={isExplore || isTour || isPanelOpen}
      />
      <GravityCursor />
      <Suspense fallback={<TourSlot />}>
        <TourOverlay />
      </Suspense>
      <Suspense fallback={<ExplorationToastSlot />}>
        <ExplorationToast />
      </Suspense>
      <Suspense fallback={<ExplorationCardSlot />}>
        <ExplorationInfoCard />
      </Suspense>

      {isExplore && <FreeOrbitPill />}

      <ScrollCue isVisible={!isExplore && !isTour && showHUD.has('scroll_cue')} />

      <IntroKicker
        visible={!isExplore && showHUD.has('intro_kicker') && !isTour}
      />

      <SceneTypography
        scene={activeScene}
        visible={!isExplore && !isTour && activeScene.id > 0}
      />

      {!isExplore && showHUD.has('content_card') && (isActiveScenePanelOpen || isTour) && (
        <AboutPanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}
      {!isExplore && showHUD.has('tag_cloud_card') && (isActiveScenePanelOpen || isTour) && (
        <SkillsPanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}
      {!isExplore && showHUD.has('satellite_info_card') && (
        <SatelliteInfoCard
          activeScene={activeScene}
          openByDefault={
            isActiveScenePanelOpen || isTour
          }
        />
      )}
      {!isExplore && showHUD.has('fragment_list_card') && (
        <NoteDetailCard
          activeScene={activeScene}
          openByDefault={
            isActiveScenePanelOpen || isTour
          }
        />
      )}
      {!isExplore && showHUD.has('milestone_card') && (isActiveScenePanelOpen || isTour) && (
        <TimelinePanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}
      {!isExplore && showHUD.has('contact_card') && (isActiveScenePanelOpen || isTour) && (
        <ContactPanel
          activeScene={activeScene}
          copyLink={shareUrl}
          onClose={closePanel}
        />
      )}

      {/* Backdrop — visible only when a modal panel is open. */}
      <button
        type="button"
        aria-label="Close panel"
        className={['hud-backdrop', isPanelOpen ? 'is-open' : ''].join(' ')}
        onClick={closePanel}
        tabIndex={-1}
      />

      {/* The deep-dive detail overlay — sits on top of everything.
          Lazy-loaded: only fetched on first open, so the deep-dive
          component + its motion/animation code don't tax the initial
          bundle. */}
      <Suspense fallback={<PanelSlot />}>
        <DetailPanel />
      </Suspense>

      {/* Bottom-right ambient audio toggle. Lazy-loads the audio
          engine + WebAudio synthesis code on first paint. */}
      <Suspense fallback={<AmbientSlot />}>
        <AmbientAudioController />
      </Suspense>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FreeOrbitPill — replaces ChapterPill in exploration mode on mobile.
// Shows "FREE ORBIT" so the user knows what mode they're in.
// ---------------------------------------------------------------------------

function FreeOrbitPill() {
  return (
    <div
      className="chapter-pill chapter-pill--free-orbit"
      role="group"
      aria-label="Mode indicator"
    >
      <span
        className="chapter-pill__index"
        aria-live="polite"
        aria-atomic="true"
      >
        FREE ORBIT
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// IntroKicker — opening composition: a cycling multilingual "hello"
// over a steady "yhazrin". Crossfades language/typeface in place
// (no rise / blur / scale). Fades out with the first scroll.
// ---------------------------------------------------------------------------

const HELLO_VARIANTS: ReadonlyArray<{
  text: string;
  lang: string;
  font: string;
  weight?: number;
  style?: 'normal' | 'italic';
}> = [
  {
    text: 'hello',
    lang: 'en',
    font: '"Fraunces", "New York", ui-serif, Georgia, serif',
    weight: 500,
  },
  {
    text: '你好',
    lang: 'zh',
    font: '"Songti SC", "Noto Serif SC", "STSong", serif',
    weight: 600,
  },
  {
    text: 'bonjour',
    lang: 'fr',
    font: '"Cormorant Garamond", "New York", ui-serif, Georgia, serif',
    weight: 500,
    style: 'italic',
  },
  {
    text: 'こんにちは',
    lang: 'ja',
    font: '"Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", serif',
    weight: 500,
  },
  {
    text: 'hola',
    lang: 'es',
    font: '"Playfair Display", "New York", ui-serif, Georgia, serif',
    weight: 500,
  },
  {
    text: 'hallo',
    lang: 'de',
    font: '"Instrument Serif", "New York", ui-serif, Georgia, serif',
    weight: 400,
    style: 'italic',
  },
  {
    text: 'ciao',
    lang: 'it',
    font: '"Cormorant Garamond", "New York", ui-serif, Georgia, serif',
    weight: 600,
  },
  {
    text: '안녕하세요',
    lang: 'ko',
    font: '"Apple SD Gothic Neo", "Noto Serif KR", "Malgun Gothic", serif',
    weight: 500,
  },
  {
    text: 'olá',
    lang: 'pt',
    font: '"Fraunces", "New York", ui-serif, Georgia, serif',
    weight: 500,
    style: 'italic',
  },
  {
    text: 'salut',
    lang: 'ro',
    font: '"Playfair Display", "New York", ui-serif, Georgia, serif',
    weight: 500,
    style: 'italic',
  },
];

/** Hold each greeting before morphing to the next. */
const HELLO_HOLD_MS = 2600;
/** Soft in-place crossfade between greetings. */
const HELLO_CROSS_MS = 900;

function helloStyle(v: (typeof HELLO_VARIANTS)[number]): CSSProperties {
  return {
    fontFamily: v.font,
    fontWeight: v.weight ?? 500,
    fontStyle: v.style ?? 'normal',
  };
}

function IntroKicker({ visible }: { visible: boolean }) {
  const progress = useIntroProgress();
  const [helloIdx, setHelloIdx] = useState(0);
  const [outgoingIdx, setOutgoingIdx] = useState<number | null>(null);
  const [crossing, setCrossing] = useState(false);
  const slotRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  // Keep the hello slot width locked to the wider of current/outgoing
  // so the signature line below doesn't jump during the morph.
  useEffect(() => {
    if (!visible) return;
    const slot = slotRef.current;
    const measure = measureRef.current;
    if (!slot || !measure) return;

    const current = HELLO_VARIANTS[helloIdx];
    const outgoing =
      outgoingIdx !== null ? HELLO_VARIANTS[outgoingIdx] : null;
    if (!current) return;

    const apply = (el: HTMLElement, v: (typeof HELLO_VARIANTS)[number]) => {
      el.textContent = v.text;
      el.style.fontFamily = v.font;
      el.style.fontWeight = String(v.weight ?? 500);
      el.style.fontStyle = v.style ?? 'normal';
    };

    apply(measure, current);
    let width = measure.offsetWidth;
    if (outgoing) {
      apply(measure, outgoing);
      width = Math.max(width, measure.offsetWidth);
    }
    slot.style.width = `${width}px`;
  }, [visible, helloIdx, outgoingIdx]);

  useEffect(() => {
    if (!visible) return;
    if (typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;

    let crossTimer: number | null = null;
    const id = window.setInterval(() => {
      setHelloIdx((i) => {
        setOutgoingIdx(i);
        return (i + 1) % HELLO_VARIANTS.length;
      });
      setCrossing(true);
      crossTimer = window.setTimeout(() => {
        setOutgoingIdx(null);
        setCrossing(false);
      }, HELLO_CROSS_MS);
    }, HELLO_HOLD_MS);

    return () => {
      window.clearInterval(id);
      if (crossTimer !== null) window.clearTimeout(crossTimer);
    };
  }, [visible]);

  if (!visible) return null;
  const fade = Math.max(0, 1 - progress * 1.4);
  const intro = HUD_PANELS.intro;
  const current = HELLO_VARIANTS[helloIdx] ?? HELLO_VARIANTS[0];
  const outgoing =
    outgoingIdx !== null ? (HELLO_VARIANTS[outgoingIdx] ?? null) : null;

  return (
    <div className="intro-hero" style={{ opacity: fade }}>
      <p className="intro-hero__byline">{intro.byline}</p>
      <h1 className="intro-hero__name">
        <span
          ref={slotRef}
          className={[
            'intro-hero__hello-slot',
            crossing ? 'is-crossing' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-live="polite"
        >
          {/* In-flow sizer keeps the line height stable while layers dissolve. */}
          <span
            className="intro-hero__hello-sizer"
            aria-hidden="true"
            style={helloStyle(current)}
          >
            {current.text}
          </span>
          {outgoing ? (
            <span
              key={`out-${outgoingIdx}`}
              className="intro-hero__hello intro-hero__hello--out"
              lang={outgoing.lang}
              style={helloStyle(outgoing)}
            >
              {outgoing.text}
            </span>
          ) : null}
          <span
            key={`in-${helloIdx}`}
            className={[
              'intro-hero__hello',
              'intro-hero__hello--in',
              crossing ? 'is-entering' : 'is-settled',
            ].join(' ')}
            lang={current.lang}
            style={helloStyle(current)}
          >
            {current.text}
          </span>
          {/* Off-screen measurer for width morph. */}
          <span
            ref={measureRef}
            className="intro-hero__hello-measure"
            aria-hidden="true"
          />
        </span>
        <span className="intro-hero__word intro-hero__signature">
          <Letters text="yhazrin" base={0} />
        </span>
      </h1>
      {/* Screen-reader stable name — cycling hello is decorative. */}
      <span className="sr-only">hello, yhazrin</span>
      <span className="intro-hero__meta">{intro.meta}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AboutPanel — chapter 1.
// ---------------------------------------------------------------------------

function AboutPanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  const data = HUD_PANELS.about;
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="About yhazrin"
      readMore={{ type: 'about', id: 'about', label: 'Read more' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">About</div>
      <h2 className="hud-panel__title">{data.name}</h2>
      <p className="hud-panel__meta">
        {data.meta.location} · {data.meta.stack} · since {data.meta.since}
      </p>
      <p className="hud-panel__body">{data.bio}</p>
      <div className="hud-panel__divider" />
      <div className="hud-panel__tags">
        {data.tags.map((tag) => (
          <Magnetic
            key={tag}
            as="span"
            className="hud-panel__tag"
            innerClassName="hud-panel__tag-inner"
            ariaLabel={`Tag: ${tag}`}
          >
            {tag}
          </Magnetic>
        ))}
      </div>
    </HUDPanel>
  );
}

// ---------------------------------------------------------------------------
// SkillsPanel — chapter 2.
// ---------------------------------------------------------------------------

function SkillsPanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  const data = HUD_PANELS.skills;
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="Skills"
      readMore={{ type: 'skills', id: 'skills', label: 'See all' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">Skills</div>
      <h2 className="hud-panel__title">{activeScene.title}</h2>
      <p className="hud-panel__subtitle">{activeScene.subtitle}</p>
      <div className="hud-panel__divider" />
      {data.categories.map((cat) => (
        <div key={cat.name} style={{ marginBottom: 14 }}>
          <p className="hud-panel__meta" style={{ marginBottom: 6 }}>
            {cat.name}
          </p>
          <div className="hud-panel__tags">
            {cat.items.map((it) => (
              <Magnetic
                key={it}
                as="span"
                className="hud-panel__tag"
                innerClassName="hud-panel__tag-inner"
                ariaLabel={`Skill: ${it}`}
              >
                {it}
              </Magnetic>
            ))}
          </div>
        </div>
      ))}
    </HUDPanel>
  );
}

// ---------------------------------------------------------------------------
// TimelinePanel — chapter 5.
// ---------------------------------------------------------------------------

function TimelinePanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="Timeline"
      readMore={{ type: 'milestone', id: TIMELINE_MILESTONES[0]?.year ?? '2019', label: 'Read story' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">Timeline</div>
      <h2 className="hud-panel__title">{activeScene.title}</h2>
      <p className="hud-panel__subtitle">{activeScene.subtitle}</p>
      <div className="hud-panel__divider" />
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'grid',
          gap: 12,
        }}
      >
        {TIMELINE_MILESTONES.map((m) => (
          <li
            key={m.year}
            style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}
          >
            <span
              className="t-mono"
              style={{
                color: 'var(--accent-cool)',
                fontSize: 11,
                letterSpacing: '0.2em',
                minWidth: 56,
              }}
            >
              {m.year}
            </span>
            <Magnetic
              as="span"
              className="hud-panel__milestone-row"
              innerClassName="hud-panel__milestone-row-inner"
              ariaLabel={`${m.year}: ${m.label}`}
            >
              {m.label}
            </Magnetic>
          </li>
        ))}
      </ul>
    </HUDPanel>
  );
}

// ---------------------------------------------------------------------------
// ContactPanel — chapter 6.
// ---------------------------------------------------------------------------

function ContactPanel({
  activeScene,
  copyLink,
  onClose,
}: {
  activeScene: SceneConfig;
  copyLink?: string | null;
  onClose: () => void;
}) {
  const items = [
    { label: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}`, target: undefined as string | undefined, rel: undefined as string | undefined },
    { label: 'GitHub', value: CONTACT.github.replace(/^https?:\/\//, ''), href: CONTACT.github, target: '_blank', rel: 'noopener noreferrer' },
    { label: 'Mastodon', value: CONTACT.mastodon.replace(/^https?:\/\//, ''), href: CONTACT.mastodon, target: '_blank', rel: 'noopener noreferrer' },
    { label: 'RSS', value: CONTACT.rss, href: CONTACT.rss, target: undefined, rel: undefined },
  ];
  return (
    <HUDPanel
      side="left"
      open
      onClose={onClose}
      ariaLabel="Contact"
      readMore={{ type: 'contact', id: 'contact', label: 'View details' }}
      copyLink={copyLink}
    >
      <div className="hud-panel__kicker">Contact</div>
      <h2 className="hud-panel__title">{activeScene.title}</h2>
      <p className="hud-panel__subtitle">{CONTACT.copy}</p>
      <div className="hud-panel__divider" />
      <ul
        style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}
      >
        {items.map((it) => (
          <li key={it.label} className="hud-panel__contact-row">
            <span className="hud-panel__meta" style={{ marginBottom: 0 }}>
              {it.label}
            </span>
            <Magnetic
              as="a"
              href={it.href}
              target={it.target}
              rel={it.rel}
              className="hud-panel__contact-value"
              innerClassName="hud-panel__contact-value-inner"
              ariaLabel={`${it.label}: ${it.value}`}
            >
              <span>{it.value}</span>
            </Magnetic>
          </li>
        ))}
      </ul>
    </HUDPanel>
  );
}

// Public — re-export the bus so other components (SatelliteMesh, NoteMesh)
// can dispatch open-panel events without an extra import path.
export const openPlanetPanel = (id: string | null) => emitPanelOpen(id);
